import { handleApiError } from '@/handleApiError'
import useCategories from '@/modules/categories/useCategories'
import useTopi from '@/modules/topi/useTopi'
import { trackingForUser } from '@/modules/tracking/events/helper/userTrackingHelper'
import { trackEvent } from '@/modules/tracking/useTracking'
import api from '@/modules/user/api'
import useUser from '@/modules/user/useUser'
import i18n, { isSupportedLocale } from '@/plugins/i18n'
import { FALLBACK_LOCALE, Language, Locale } from '@/types'
import { ContactTitle, Department, ProductSearchView, SettingPatch, User } from '@/types/index'
import { useQueryCache } from '@pinia/colada'
import * as Sentry from '@sentry/vue'
import axios from 'axios'
import { defineStore } from 'pinia'
import { Ref } from 'vue'

interface OwnUserState {
  ownUser: User
  loading: boolean
}

const initialState = (): OwnUserState => {
  return {
    ownUser: {
      id: '',
      title: ContactTitle.UNSPECIFIED,
      firstName: '',
      lastName: '',
      email: '',
      avatarUrl: '',
      alpha2language: Language.de,
      companyName: '',
      department: Department.OTHER,
      phone: '',
      phoneMobile: '',
      permission: {
        submitOrder: true,
        shopAdmin: false,
        changeSubsidiary: false,
        isApprover: false
      },
      setting: {
        preferedProductSearchView: ProductSearchView.CARDS,
        allowTracking: true
      }
    },
    loading: false
  }
}

const useOwnUser = defineStore('ownUser', {
  state: () => initialState(),
  getters: {
    isOwnUser: (state) => {
      return (id: string) => id === state.ownUser.id
    },
    isAdmin: (state) => {
      return () => state.ownUser.permission.shopAdmin
    },
    isApprover: (state) => {
      return () => state.ownUser.permission.isApprover
    },
    getLocaleOrFallback: (state) => {
      return (): Locale => {
        if (isSupportedLocale(state.ownUser.alpha2language)) {
          return state.ownUser.alpha2language
        } else {
          return FALLBACK_LOCALE
        }
      }
    }
  },
  actions: {
    updateOwnUser(update: User) {
      if (this.isOwnUser(update.id)) {
        Object.assign(this.ownUser, update)
        ;(i18n.global.locale as unknown as Ref<string>).value = this.getLocaleOrFallback()
        axios.defaults.headers.common['Accept-Language'] = this.getLocaleOrFallback()
        useCategories().$reset()
        const queryCache = useQueryCache()
        queryCache.invalidateQueries()
        if (useTopi().isInitialized()) {
          useTopi().initTopi(this.getLocaleOrFallback())
        }
      }
    },
    async initUserSession() {
      return api
        .getOwnUser()
        .then(({ data }) => {
          this.ownUser = data
          ;(i18n.global.locale as unknown as Ref<string>).value = this.getLocaleOrFallback()
          axios.defaults.headers.common['Accept-Language'] = this.getLocaleOrFallback()
          Sentry.getCurrentScope().setUser({
            email: data.email
          })
        })
        .catch(handleApiError)
    },
    async updateUserSettings(dto: SettingPatch) {
      return api.updateUserSettings(this.ownUser.id, dto).catch(handleApiError)
    },
    async updateAllowTracking(allowTracking: boolean) {
      const event = trackingForUser(this.ownUser.id).allowTrackingSetting(allowTracking)

      if (allowTracking) {
        // when enabling tracking first update the setting and then track the event
        await useUser().updateSettings(this.ownUser.id, { allowTracking })
        return trackEvent(event)
      } else {
        // when disabling tracking first track the event and then update the setting
        await trackEvent(event)
        return useUser().updateSettings(this.ownUser.id, { allowTracking })
      }
    }
  }
})

export default useOwnUser
